<template>
  <app-modal v-model="modal" persistent max-width="500px">
    <template v-slot:title>
      {{formConfig.title}}
    </template>
    <template v-slot:alert>
      <v-alert outline color="warning" icon="warning" :value="shouldDisplayUpsertWarn">{{$t('ContinuousApprovalLevelManage.upsertWarning', { msg: formApprovalLevelConfiguration.MappedValue || 'Default'})}}</v-alert>
    </template>
    <template v-slot:text>
      <v-form ref="form" v-model="formIsValidated">
        <v-checkbox :label="$t('ContinuousApprovalLevelManage.defaultApprovalLevel')" :readonly="!isAddForm" v-model="formIsDefaultApprovalLevelConfiguration"></v-checkbox>
        <v-text-field :label="$t('ContinuousApprovalLevelManage.continuousDatabaseValue')" :rules="databaseValueRules" v-model="formApprovalLevelConfiguration.MappedValue" :disabled="!isAddForm || formIsDefaultApprovalLevelConfiguration"></v-text-field>
        <v-text-field :label="$t('ContinuousApprovalLevelManage.outputText')" :rules="outputTextRules" v-model="formApprovalLevelConfiguration.OutputText"></v-text-field>
        <v-textarea :label="$t('ContinuousApprovalLevelManage.outputDescription')" v-model="formApprovalLevelConfiguration.OutputDescription">
        </v-textarea>
      </v-form>
    </template>
    <template v-slot:actions>
      <v-spacer></v-spacer>
      <v-btn color="success" @click="save" :disabled="!formIsValidated" :loading="isSaving">
        <v-icon>done</v-icon>
        {{ $t('Button.Save') }}
      </v-btn>
      <v-btn color="secondary" @click="close">{{ $t('Button.Cancel') }}</v-btn>
    </template>
  </app-modal>
</template>

<script>
  import FormValidationUtil from '@/components/mixins/FormValidationUtil'
  import {
    createNamespacedHelpers,
  } from 'vuex'
  const approvalLevels = createNamespacedHelpers('approvalLevels')
  const app = createNamespacedHelpers('app')
  export default {
    mixins: [FormValidationUtil],
    props: {
      value: {
        type: Boolean,
        default: false,
      },
      approvalLevelConfiguration: {
        value: {
          type: Object,
          default: () => null,
        },
      },
      // required when systemDefault is false
      tenantId: {
        type: Number,
        default: -1,
      },
      systemDefault: {
        type: Boolean,
        default: false,
      },
      // required when systemDefault is true
      continuousDatabaseType: {
        type: String,
      },
    },
    data() {
      return {
        formApprovalLevelConfiguration: {},
        formIsDefaultApprovalLevelConfiguration: false,
        formIsValidated: true,
        modal: false,
        databaseValueRules: [
          v => (this.rules.numeric(v) || (this.formIsDefaultApprovalLevelConfiguration && !v)) || 'Value must be a number',
        ],
        outputTextRules: [
          v => this.rules.required(v) || 'Required field',
        ],
        tenantFormConfigs: {
          addForm: {
            title: this.$t('ContinuousApprovalLevelManage.addTenantApprovalLevelFormTitle'),
            successMessage: this.$t('ContinuousApprovalLevelManage.addSuccessMessage'),
            errorMessageFn: (msg) => this.$t('ContinuousApprovalLevelManage.addErrorMessage', { msg }),
          },
          updateForm: {
            title: this.$t('ContinuousApprovalLevelManage.updateTenantApprovalLevelFormTitle'),
            successMessage: this.$t('ContinuousApprovalLevelManage.updateSuccessMessage'),
            errorMessageFn: (msg) => this.$t('ContinuousApprovalLevelManage.updateErrorMessage', { msg }),
          },
        },
        systemDefaultFormConfigs: {
          addForm: {
            title: this.$t('ContinuousApprovalLevelManage.addSystemDefaultApprovalLevelFormTitle'),
            successMessage: this.$t('ContinuousApprovalLevelManage.addSuccessMessage'),
            errorMessageFn: (msg) => this.$t('ContinuousApprovalLevelManage.addErrorMessage', { msg }),
          },
          updateForm: {
            title: this.$t('ContinuousApprovalLevelManage.updateSystemDefaultApprovalLevelFormTitle'),
            successMessage: this.$t('ContinuousApprovalLevelManage.updateSuccessMessage'),
            errorMessageFn: (msg) => this.$t('ContinuousApprovalLevelManage.updateErrorMessage', { msg }),
          },
        },
        isSaving: false,
      }
    },
    computed: {
      ...approvalLevels.mapState(['tenantApprovalLevelConfigurations', 'systemDefaultApprovalLevelConfigurations']),
      isAddForm() {
        return this.approvalLevelConfiguration == null
      },
      formConfig() {
        const configSet = this.systemDefault ? this.systemDefaultFormConfigs : this.tenantFormConfigs
        return this.isAddForm ? configSet.addForm : configSet.updateForm
      },
      existingConfigurations() {
        return this.systemDefault ? this.systemDefaultApprovalLevelConfigurations : this.tenantApprovalLevelConfigurations
      },
      shouldDisplayUpsertWarn() {
        const mappedValueAlreadyExists = this.existingConfigurations.findIndex(x => x.MappedValue == this.formApprovalLevelConfiguration.MappedValue) >= 0
        return this.isAddForm && this.formIsValidated && mappedValueAlreadyExists
      },
    },
    watch: {
      value: function() {
        this.modal = this.value
        if (!this.value) {
          this.reset()
        }
      },
      modal: function() {
        this.$emit('input', this.modal)
      },
      formIsDefaultApprovalLevelConfiguration: function() {
        if (this.isAddForm) {
          this.formApprovalLevelConfiguration.MappedValue = null
          this.resetValidation()
        }
      },
      approvalLevelConfiguration: function() {
        this.initializeForm()
      },
    },
    mounted() {
      this.modal = this.value
      this.initializeForm()
    },
    methods: {
      ...app.mapActions(['setSnackbar']),
      ...approvalLevels.mapActions([
        'getTenantApprovalLevelConfigurations', 'upsertTenantApprovalLevelConfiguration',
        'getSystemDefaultApprovalLevelConfigurations', 'upsertSystemDefaultApprovalLevelConfiguration',
      ]),
      async save() {
        try {
          this.isSaving = true
          if (this.systemDefault) {
            await this.saveSystemDefaultConfig()
          } else {
            await this.saveTenantConfig()
          }
          this.setSnackbar({
            icon: 'done',
            color: 'success',
            text: this.formConfig.successMessage,
          })
          this.close()
        } catch (ex) {
          const exResponse = ex && ex.response && ex.response.data
          this.setSnackbar({
            icon: 'error',
            text: this.formConfig.errorMessageFn(exResponse && (exResponse.ExceptionMessage || exResponse.Message)),
            color: 'error',
          })
        } finally {
          this.isSaving = false
        }
      },
      async saveTenantConfig() {
        await this.upsertTenantApprovalLevelConfiguration({ configuration: this.formApprovalLevelConfiguration })
        await this.getTenantApprovalLevelConfigurations({ tenantId: this.tenantId })
      },
      async saveSystemDefaultConfig() {
        await this.upsertSystemDefaultApprovalLevelConfiguration({ configuration: this.formApprovalLevelConfiguration, continuousDatabaseType: this.continuousDatabaseType })
        await this.getSystemDefaultApprovalLevelConfigurations({ continuousDatabaseType: this.continuousDatabaseType })
      },
      reset() {
        this.$refs.form.reset()
      },
      resetValidation() {
        this.$refs.form.resetValidation()
      },
      close() {
        this.reset()
        this.$emit('input', false)
      },
      initializeForm() {
        if (this.isAddForm) {
          this.formApprovalLevelConfiguration = this.initiateApprovalLevelConfiguration()
          this.resetValidation()
        } else {
          this.formApprovalLevelConfiguration = JSON.parse(JSON.stringify(this.approvalLevelConfiguration))
          this.formIsDefaultApprovalLevelConfiguration = !this.approvalLevelConfiguration.MappedValue
          this.resetValidation()
        }
      },
      initiateApprovalLevelConfiguration() {
        let initialConfig = {
          MappedValue: null,
          OutputText: '',
          OutputDescription: '',
        }
        if (!this.systemDefault) {
          initialConfig.TenantId = this.tenantId
        }
        return initialConfig
      },
    },
  }
</script>